import React, { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchBlockSummary } from '../../services/api';

const NAME_KEYS = ['Metric', 'KPI', 'Name', 'Item', 'Category', 'Period', 'Month', 'Label', 'Description', 'id', 'ID'];

const toNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const sanitized = value.replace(/,/g, '').trim();
    if (!sanitized) return null;
    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatDisplay = (value) => (value == null || value === '' ? '-' : String(value));

const getLabelFromRow = (row, fallback) => {
  for (const key of NAME_KEYS) {
    if (row[key] != null && String(row[key]).trim() !== '') {
      return String(row[key]);
    }
  }
  return fallback;
};

const getPeriodLabelFromRow = (row, fallback) => {
  const periodKeys = ['Period', 'period', 'Month', 'month', 'Date', 'date'];
  for (const key of periodKeys) {
    if (row[key] != null && String(row[key]).trim() !== '') {
      return String(row[key]);
    }
  }
  return fallback;
};

const normalizeMetricKey = (key) =>
  String(key || '')
    .toLowerCase()
    .replace(/\(actual\)|\(plan\)|\(budget\)|\(target\)|\(act\)/g, '')
    .replace(/actual|plan|budget|target|act/g, '')
    .replace(/[^a-z0-9]/g, '');

const prettifyMetricName = (key) =>
  String(key || '')
    .replace(/\((actual|plan|budget|target|act)\)/gi, '')
    .replace(/\b(actual|plan|budget|target|act)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

const extractActualPlanPairsFromRow = (row) => {
  const keys = Object.keys(row || {});
  const actualKeys = keys.filter((k) => /actual|act/i.test(k));
  const planKeys = keys.filter((k) => /plan|budget|target/i.test(k));
  const pairs = [];

  actualKeys.forEach((actualKey) => {
    const actual = toNumber(row[actualKey]);
    if (actual == null) return;

    const actualBase = normalizeMetricKey(actualKey);
    const matchingPlanKey = planKeys.find((planKey) => {
      const plan = toNumber(row[planKey]);
      if (plan == null) return false;
      return normalizeMetricKey(planKey) === actualBase;
    });

    if (!matchingPlanKey) return;

    const plan = toNumber(row[matchingPlanKey]);
    if (plan == null) return;

    pairs.push({
      metric: prettifyMetricName(actualKey) || 'Metric',
      actual,
      plan
    });
  });

  return pairs;
};

const getPreferredPair = (row) => {
  const pairs = extractActualPlanPairsFromRow(row);
  if (!pairs.length) return null;

  const priorities = ['drilled', 'volume', 'tonnage', 'content', 'grade'];
  const scored = pairs.map((pair) => {
    const key = pair.metric.toLowerCase().replace(/\s+/g, '');
    const scoreIndex = priorities.findIndex((token) => key.includes(token));
    return { pair, score: scoreIndex === -1 ? 999 : scoreIndex };
  });

  scored.sort((a, b) => a.score - b.score);
  return scored[0].pair;
};

const buildActualVsPlanRows = (resultSets, selectedMetricKey = 'auto') => {
  const rows = [];
  resultSets.forEach((set, setIndex) => {
    (set.rows || []).forEach((row, rowIndex) => {
      const pairs = extractActualPlanPairsFromRow(row);
      if (!pairs.length) return;

      let pair = null;
      if (selectedMetricKey === 'auto') {
        pair = getPreferredPair(row);
      } else {
        pair = pairs.find((p) => normalizeMetricKey(p.metric) === selectedMetricKey) || null;
      }
      if (!pair) return;

      const metricLabel = getLabelFromRow(row, pair.metric || `${set.name || 'Summary'} #${rowIndex + 1}`);
      rows.push({
        label: metricLabel,
        metric: pair.metric || metricLabel,
        period: getPeriodLabelFromRow(row, metricLabel),
        actual: pair.actual,
        plan: pair.plan,
        variancePct: pair.plan === 0 ? null : ((pair.actual - pair.plan) / pair.plan) * 100,
        source: set.name || `ResultSet${setIndex + 1}`
      });
    });
  });
  return rows;
};

export default function BlockObjectDetailsView({ blockId, formatNumericByLabel }) {
  const [blockSummary, setBlockSummary] = useState({
    loading: false,
    error: '',
    blockId: '',
    resultSets: []
  });
  const [selectedGraphMetric, setSelectedGraphMetric] = useState('auto');

  const displayValue = (value, label) =>
    typeof formatNumericByLabel === 'function' ? formatNumericByLabel(value, label) : formatDisplay(value);

  const formatVariancePct = (value) => {
    const numeric = toNumber(value);
    if (numeric == null) return '-';
    return `${Math.round(numeric).toLocaleString()}%`;
  };

  const getVariancePctFromRow = (row) => {
    const pair = getPreferredPair(row);
    if (!pair || pair.plan === 0) return null;
    return ((pair.actual - pair.plan) / pair.plan) * 100;
  };

  const getCellDisplayValue = (row, column) => {
    const normalized = String(column || '').toLowerCase().replace(/\s+/g, '');
    if (normalized.includes('variance')) {
      const computedPct = getVariancePctFromRow(row);
      if (computedPct != null) return formatVariancePct(computedPct);
      const rawNumeric = toNumber(row[column]);
      if (rawNumeric != null && normalized.includes('variancepct')) return formatVariancePct(rawNumeric);
      return '-';
    }
    return displayValue(row[column], column);
  };

  useEffect(() => {
    let isCancelled = false;

    const loadSummary = async () => {
      if (!blockId) {
        setBlockSummary({ loading: false, error: '', blockId: '', resultSets: [] });
        return;
      }

      setBlockSummary((prev) => ({
        ...prev,
        loading: true,
        error: '',
        blockId,
        resultSets: []
      }));

      const response = await fetchBlockSummary(blockId);
      if (isCancelled) return;

      if (!response?.success) {
        setBlockSummary({
          loading: false,
          error: response?.error || 'Failed to load block summary.',
          blockId,
          resultSets: []
        });
        return;
      }

      setBlockSummary({
        loading: false,
        error: '',
        blockId,
        resultSets: Array.isArray(response.resultSets) ? response.resultSets : []
      });
    };

    loadSummary();
    return () => {
      isCancelled = true;
    };
  }, [blockId]);

  const chartRowsBySet = useMemo(
    () =>
      (blockSummary.resultSets || []).map((set, setIndex) => ({
        name: set.name || `Dataset ${setIndex + 1}`,
        rows: buildActualVsPlanRows([set], selectedGraphMetric).slice(0, 24)
      })),
    [blockSummary.resultSets, selectedGraphMetric]
  );

  const allActualVsPlanRows = useMemo(
    () => chartRowsBySet.flatMap((setChart) => setChart.rows),
    [chartRowsBySet]
  );

  const totals = useMemo(() => {
    if (!allActualVsPlanRows.length) return null;
    const totalActual = allActualVsPlanRows.reduce((sum, row) => sum + row.actual, 0);
    const totalPlan = allActualVsPlanRows.reduce((sum, row) => sum + row.plan, 0);
    return {
      totalActual,
      totalPlan,
      variancePct: totalPlan === 0 ? null : ((totalActual - totalPlan) / totalPlan) * 100
    };
  }, [allActualVsPlanRows]);

  const metricOptions = useMemo(() => {
    const metricsMap = new Map();
    (blockSummary.resultSets || []).forEach((set) => {
      (set.rows || []).forEach((row) => {
        extractActualPlanPairsFromRow(row).forEach((pair) => {
          const key = normalizeMetricKey(pair.metric);
          if (key && !metricsMap.has(key)) {
            metricsMap.set(key, pair.metric);
          }
        });
      });
    });
    return Array.from(metricsMap.entries()).map(([key, label]) => ({ key, label }));
  }, [blockSummary.resultSets]);

  return (
    <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-3">
      <h3 className="text-lg font-semibold text-gray-700">Block Summary ({blockSummary.blockId})</h3>
      <p className="text-xs text-gray-500 mb-3">Source: sp_GetBlockSummary | View: Actual vs Plan</p>

      {blockSummary.loading && <div className="text-sm text-gray-600">Loading block summary...</div>}

      {!blockSummary.loading && blockSummary.error && (
        <div className="text-sm text-red-600">{blockSummary.error}</div>
      )}

      {!blockSummary.loading && !blockSummary.error && (
        <>
          {totals && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              <div className="rounded border border-gray-200 p-2">
                <p className="text-xs text-gray-500">Total Actual</p>
                <p className="text-base font-semibold text-gray-800">{totals.totalActual.toLocaleString()}</p>
              </div>
              <div className="rounded border border-gray-200 p-2">
                <p className="text-xs text-gray-500">Total Plan</p>
                <p className="text-base font-semibold text-gray-800">{totals.totalPlan.toLocaleString()}</p>
              </div>
              <div className="rounded border border-gray-200 p-2">
                <p className="text-xs text-gray-500">Variance %</p>
                <p className={`text-base font-semibold ${(totals.variancePct || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatVariancePct(totals.variancePct)}
                </p>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="text-xs text-gray-600 mr-2">Graph Metric</label>
            <select
              value={selectedGraphMetric}
              onChange={(e) => setSelectedGraphMetric(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
            >
              <option value="auto">Auto</option>
              {metricOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {allActualVsPlanRows.length === 0 && (
            <div className="text-sm text-gray-600 mb-4">
              No numeric Actual vs Plan pairs were found in the summary result.
            </div>
          )}

          {blockSummary.resultSets.map((set, setIndex) => {
            const setRows = Array.isArray(set.rows) ? set.rows : [];
            const columns = setRows.length ? Object.keys(setRows[0]) : [];
            const setChartRows = chartRowsBySet[setIndex]?.rows || [];
            return (
              <div key={`${set.name || 'ResultSet'}-${setIndex}`} className="mb-4 border border-gray-200 rounded-md">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-700">
                  {set.name || `ResultSet${setIndex + 1}`}
                </div>
                {setChartRows.length > 0 && (
                  <div className="px-3 pt-3">
                    <p className="text-xs text-gray-500 mb-2">
                      {set.name || `Dataset ${setIndex + 1}`} - Actual vs Plan by Period
                    </p>
                    <div className="h-64 w-full mb-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={setChartRows}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={70} />
                          <YAxis />
                          <Tooltip
                            formatter={(value, name, entry) => {
                              const metricLabel = entry?.payload?.metric || entry?.payload?.period || '';
                              return [displayValue(value, metricLabel), name === 'actual' ? 'Actual' : 'Plan'];
                            }}
                            labelFormatter={(label) => `Period: ${label}`}
                          />
                          <Legend />
                          <Bar dataKey="actual" fill="#2563eb" name="Actual" />
                          <Bar dataKey="plan" fill="#f59e0b" name="Plan" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto p-3">
                  {setRows.length === 0 ? (
                    <p className="text-xs text-gray-500">No rows returned.</p>
                  ) : (
                    <table className="w-full table-auto text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          {columns.map((column) => (
                            <th key={column} className="px-2 py-1 text-left font-medium text-gray-700 border-b border-gray-200">
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {setRows.map((row, rowIndex) => (
                          <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            {columns.map((column) => (
                              <td key={column} className="px-2 py-1 border-b border-gray-100">
                                {getCellDisplayValue(row, column)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
